import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Space, Modal, message, Progress, Row, Col, Typography, Tag, Input, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, BarChartOutlined, MinusOutlined, SearchOutlined, SortAscendingOutlined, SortDescendingOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, deleteDoc, doc, updateDoc, increment, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../../../contexts/AuthContext';
import ParayanaFormModal from './ParayanaFormModal';

const { Title, Text } = Typography;
const { Search } = Input;

const ParayanaList = () => {
  const [parayanas, setParayanas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingParayana, setEditingParayana] = useState(null);
  const [updateLoading, setUpdateLoading] = useState({});
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState('recent'); // recent, nameAsc, nameDesc, progressAsc, progressDesc
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const { getUserId, getUserName, getUserEmail } = useAuth();

  // Responsive detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fallbackFetch = async (userId) => {
    try {
      const q = query(
        collection(db, `users/${userId}/parayanas`)
      );
      const querySnapshot = await getDocs(q);
      const parayanaList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by creation date
      parayanaList.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.createdAt?.toDate?.() || new Date(0);
        return dateB - dateA;
      });
      
      setParayanas(parayanaList);
    } catch (error) {
      setParayanas([]); // Set empty array instead of showing error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let unsubscribe;
    
    const setupListener = async () => {
      const userId = getUserId();
      if (!userId) {
        console.log('⏳ No user ID available yet');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('📊 Setting up real-time listener for user:', userId);
        
        // Use real-time listener for automatic updates
        const q = query(
          collection(db, `users/${userId}/parayanas`)
        );
        
        unsubscribe = onSnapshot(q, (querySnapshot) => {
          try {
            const parayanaList = querySnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            
            // Sort by creation date (most recent first) since orderBy might fail on empty collection
            parayanaList.sort((a, b) => {
              const dateA = a.createdAt?.toDate?.() || new Date(0);
              const dateB = b.createdAt?.toDate?.() || new Date(0);
              return dateB - dateA;
            });
            
            console.log(`📋 Loaded ${parayanaList.length} parayanas for user`);
            setParayanas(parayanaList);
            setLoading(false);
          } catch (error) {
            console.error('❌ Error processing parayanas:', error);
            setParayanas([]);
            setLoading(false);
          }
        }, (error) => {
          console.error('❌ Real-time listener error:', error);
          // Fallback to regular fetch if real-time fails
          fallbackFetch(userId);
        });

        // Store unsubscribe function for cleanup
        return unsubscribe;
        
      } catch (error) {
        console.error('❌ Error setting up listener:', error);
        // Fallback to regular fetch
        fallbackFetch(userId);
      }
    };
    
    setupListener();
    
    // Cleanup function
    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [getUserId]);

  // Filter and sort parayanas
  const filteredAndSortedParayanas = useMemo(() => {
    let filtered = parayanas;

    // Apply search filter
    if (searchText.trim()) {
      filtered = filtered.filter(parayana =>
        parayana.name.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'nameAsc':
          return a.name.localeCompare(b.name);
        case 'nameDesc':
          return b.name.localeCompare(a.name);
        case 'progressAsc':
          const progressA = ((a.currentCount || 0) / a.targetCount) * 100;
          const progressB = ((b.currentCount || 0) / b.targetCount) * 100;
          return progressA - progressB;
        case 'progressDesc':
          const progressA2 = ((a.currentCount || 0) / a.targetCount) * 100;
          const progressB2 = ((b.currentCount || 0) / b.targetCount) * 100;
          return progressB2 - progressA2;
        case 'recent':
        default:
          const dateA = a.createdAt?.toDate?.() || new Date(0);
          const dateB = b.createdAt?.toDate?.() || new Date(0);
          return dateB - dateA;
      }
    });

    return sorted;
  }, [parayanas, searchText, sortBy]);

  const updateCounter = async (parayanaId, increment_value) => {
    try {
      setUpdateLoading(prev => ({ ...prev, [parayanaId]: true }));
      
      const userId = getUserId();
      if (!userId) {
        message.error('User not authenticated');
        return;
      }

      // Find the parayana to validate the operation
      const parayana = parayanas.find(p => p.id === parayanaId);
      if (!parayana) {
        message.error('Parayana not found');
        return;
      }

      // Validate increment value
      if (!Number.isInteger(increment_value) || increment_value === 0) {
        message.error('Invalid increment value');
        return;
      }

      // Prevent negative counts
      const currentCount = parayana.currentCount || 0;
      if (increment_value < 0 && currentCount + increment_value < 0) {
        message.warning('Count cannot go below zero');
        return;
      }

      // Prevent excessive counts (safety limit)
      if (increment_value > 0 && currentCount + increment_value > 999999) {
        message.warning('Count limit reached (999,999)');
        return;
      }

      // Check if parayana is already completed
      if (increment_value > 0 && currentCount >= parayana.targetCount) {
        message.info('This parayana is already completed! 🎉');
        return;
      }

      console.log(`📊 Updating counter: ${parayana.name} ${increment_value > 0 ? '+' : ''}${increment_value}`);

      // Log the transaction first
      await addDoc(collection(db, `users/${userId}/parayanas/${parayanaId}/logs`), {
        countDelta: increment_value,
        timestamp: serverTimestamp(),
        uid: userId,
        parayanaName: parayana.name // For debugging
      });

      // Then update the parayana count
      const parayanaRef = doc(db, `users/${userId}/parayanas`, parayanaId);
      await updateDoc(parayanaRef, {
        currentCount: increment(increment_value),
        lastUpdated: serverTimestamp()
      });

      // Show appropriate success message
      const newCount = currentCount + increment_value;
      const isNowCompleted = newCount >= parayana.targetCount;
      
      if (isNowCompleted && increment_value > 0) {
        message.success('🎉 Congratulations! Parayana completed! 🙏', 3);
      } else {
        message.success(increment_value > 0 ? 'Count increased! 🙏' : 'Count decreased');
      }

    } catch (error) {
      console.error('💥 Error updating counter:', error);
      
      // Provide specific error messages
      if (error.code === 'permission-denied') {
        message.error('Permission denied. Please check your connection and try again.');
      } else if (error.code === 'unavailable') {
        message.error('Service temporarily unavailable. Please try again.');
      } else if (error.code === 'quota-exceeded') {
        message.error('Daily quota exceeded. Please try again tomorrow.');
      } else {
        message.error(`Update failed: ${error.message}`);
      }
    } finally {
      setUpdateLoading(prev => ({ ...prev, [parayanaId]: false }));
    }
  };

  const handleDelete = async (id) => {
    // Find parayana to show name in confirmation
    const parayana = parayanas.find(p => p.id === id);
    const parayanaName = parayana?.name || 'this parayana';

    Modal.confirm({
      title: `Delete "${parayanaName}"?`,
      content: (
        <div>
          <p>This will permanently delete:</p>
          <ul>
            <li>All progress data</li>
            <li>Transaction history</li>
            <li>Analytics records</li>
          </ul>
          <p><strong>This action cannot be undone.</strong></p>
        </div>
      ),
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          console.log(`🗑️ Deleting parayana: ${parayanaName}`);
          
          const userId = getUserId();
          if (!userId) {
            message.error('User not authenticated');
            return;
          }

          await deleteDoc(doc(db, `users/${userId}/parayanas`, id));
          message.success(`"${parayanaName}" deleted successfully`);
          
          // Real-time listener will automatically update the list
        } catch (error) {
          console.error('💥 Error deleting parayana:', error);
          
          if (error.code === 'permission-denied') {
            message.error('Permission denied. Unable to delete.');
          } else if (error.code === 'not-found') {
            message.error('Parayana not found. It may have been already deleted.');
          } else {
            message.error(`Delete failed: ${error.message}`);
          }
        }
      },
    });
  };

  const handleModalSuccess = () => {
    setModalVisible(false);
    setEditingParayana(null);
    // Real-time listener will automatically update the list
    message.success(editingParayana ? 'Parayana updated successfully' : 'Parayana added successfully');
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 100) return '#52c41a'; // Green
    if (percentage >= 75) return '#faad14'; // Orange
    if (percentage >= 50) return '#FF8C00'; // Saffron
    if (percentage >= 25) return '#FFB347'; // Light saffron
    return '#ff7875'; // Light red
  };

  const getSortIcon = () => {
    switch (sortBy) {
      case 'nameAsc':
      case 'progressAsc':
        return <SortAscendingOutlined />;
      case 'nameDesc':
      case 'progressDesc':
        return <SortDescendingOutlined />;
      case 'recent':
      default:
        return <ClockCircleOutlined />;
    }
  };

  const ParayanaCard = ({ parayana }) => {
    const currentCount = parayana.currentCount || 0;
    const progress = Math.min((currentCount / parayana.targetCount) * 100, 100);
    const isCompleted = currentCount >= parayana.targetCount;

    // Determine card colors based on progress
    const getCardColors = (progress) => {
      if (progress >= 100) {
        return {
          border: '#52c41a',
          shadow: '#52c41a',
          background: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)'
        };
      } else if (progress >= 30) {
        return {
          border: '#faad14',
          shadow: '#faad14',
          background: 'linear-gradient(135deg, #fffbe6 0%, #fff7e6 100%)'
        };
      } else {
        return {
          border: '#ff7875',
          shadow: '#ff7875',
          background: 'linear-gradient(135deg, #fff2f0 0%, #ffece8 100%)'
        };
      }
    };

    const cardColors = getCardColors(progress);

    return (
      <Card
        hoverable
        style={{
          borderRadius: '16px',
          border: `2px solid ${cardColors.border}`,
          boxShadow: `0 4px 16px ${cardColors.shadow}20`,
          background: cardColors.background,
          overflow: 'hidden'
        }}
        styles={{ body: { padding: '20px' } }}
      >
        {/* Header with Name and Status */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
            <Title 
              level={4} 
              style={{ 
                margin: 0, 
                color: isCompleted ? '#52c41a' : '#FF8C00',
                cursor: 'pointer',
                fontSize: 'clamp(16px, 4vw, 20px)'
              }}
              onClick={() => navigate(`/apps/prayer-counter/${parayana.id}`)}
            >
              🕉️ {parayana.name}
            </Title>
            {isCompleted && (
              <Tag color="success" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                ✅ COMPLETED
              </Tag>
            )}
          </div>
          
          {/* Progress Bar */}
          <Progress
            percent={Math.round(progress)}
            strokeColor={getProgressColor(progress)}
            trailColor="#f0f0f0"
            strokeWidth={8}
            showInfo={false}
            style={{ marginBottom: '12px' }}
          />
          
          {/* Count Display */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <Text style={{ 
              fontSize: '16px', 
              fontWeight: 'bold',
              color: isCompleted ? '#52c41a' : progress >= 30 ? '#faad14' : '#ff7875'
            }}>
              {currentCount} / {parayana.targetCount}
            </Text>
            <Text style={{ 
              fontSize: '14px', 
              color: '#666',
              background: isCompleted ? '#f6ffed' : progress >= 30 ? '#fffbe6' : '#fff2f0',
              padding: '4px 8px',
              borderRadius: '8px',
              border: `1px solid ${isCompleted ? '#d9f7be' : progress >= 30 ? '#ffe58f' : '#ffa39e'}`
            }}>
              {Math.round(progress)}% complete
            </Text>
          </div>
        </div>

        {/* Counter Controls */}
        <div style={{ marginBottom: '16px' }}>
          <Row gutter={8} align="middle">
            <Col flex="auto">
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center',
                gap: '12px',
                alignItems: 'center'
              }}>
                <Button
                  icon={<MinusOutlined />}
                  onClick={() => updateCounter(parayana.id, -1)}
                  loading={updateLoading[parayana.id]}
                  disabled={currentCount <= 0}
                  style={{
                    borderColor: '#FF8C00',
                    color: '#FF8C00',
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  size="large"
                />
                
                <div style={{
                  background: 'linear-gradient(135deg, #FF8C00 0%, #FF7F00 100%)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '24px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  minWidth: '80px',
                  textAlign: 'center',
                  boxShadow: '0 4px 12px rgba(255, 140, 0, 0.3)'
                }}>
                  {currentCount}
                </div>
                
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => updateCounter(parayana.id, 1)}
                  loading={updateLoading[parayana.id]}
                  disabled={isCompleted}
                  style={{
                    background: 'linear-gradient(135deg, #FF8C00 0%, #FF7F00 100%)',
                    borderColor: '#FF8C00',
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  size="large"
                />
              </div>
            </Col>
          </Row>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setEditingParayana(parayana);
              setModalVisible(true);
            }}
            style={{
              borderColor: '#FFB347',
              color: '#FF8C00',
              flex: 1,
              minWidth: '80px'
            }}
            size="small"
          >
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(parayana.id)}
            style={{
              flex: 1,
              minWidth: '80px'
            }}
            size="small"
          >
            Delete
          </Button>
          <Button
            type="default"
            onClick={() => navigate(`/apps/prayer-counter/${parayana.id}`)}
            style={{
              borderColor: '#FFB347',
              color: '#FF8C00',
              flex: 1,
              minWidth: '80px'
            }}
            size="small"
          >
            Details
          </Button>
        </div>
      </Card>
    );
  };

  return (
    <div style={{
      background: 'transparent',
      minHeight: 'calc(100vh - 60px)',
      padding: '0'
    }}>
      {/* Header Actions */}
      <Card
        style={{ 
          marginBottom: 12,
          background: 'linear-gradient(135deg, #FFF4E6 0%, #FFE4B5 100%)',
          border: '1px solid #FFB347',
          borderRadius: '12px'
        }}
        styles={{ body: { padding: '12px 16px' } }}
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div>
            <Title level={3} style={{ 
              color: '#FF8C00', 
              margin: 0,
              fontSize: 'clamp(18px, 4vw, 24px)'
            }}>
              My Parayanas ({filteredAndSortedParayanas.length})
            </Title>
            <Text style={{ color: '#FF7F00', fontSize: '14px' }}>
              Track your spiritual progress
              {getUserEmail() && (
                <span style={{ marginLeft: '8px', fontSize: '12px', opacity: 0.8 }}>
                  • {getUserEmail()}
                </span>
              )}
            </Text>
          </div>
          <Space size="small">
            <Button
              icon={<BarChartOutlined />}
              onClick={() => navigate('/apps/prayer-counter/analytics')}
              style={{
                borderColor: '#FFB347',
                color: '#FF8C00'
              }}
            >
              Analytics
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingParayana(null);
                setModalVisible(true);
              }}
              style={{
                background: 'linear-gradient(135deg, #FF8C00 0%, #FF7F00 100%)',
                borderColor: '#FF8C00'
              }}
            >
              Add Parayana
            </Button>
          </Space>
        </div>
      </Card>

      {/* Search and Sort Controls */}
      <Card
        style={{ 
          marginBottom: 12,
          borderRadius: '12px',
          border: '1px solid #FFB347'
        }}
        styles={{ body: { padding: '16px' } }}
      >
        <div 
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '12px',
            alignItems: 'stretch'
          }}
        >
          {/* Search Input and Button */}
          <div style={{
            flex: '1 1 auto',
            minWidth: '200px',
            display: 'flex',
            gap: '8px',
            alignItems: 'center'
          }}>
            <Input
              placeholder="Search parayanas by name..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={() => setSearchText(searchText)}
              style={{ 
                width: '100%',
                height: '40px'
              }}
              allowClear
            />
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => setSearchText(searchText)}
              style={{
                background: 'linear-gradient(135deg, #FF8C00 0%, #FF7F00 100%)',
                borderColor: '#FF8C00',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            />
          </div>
          
          {/* Sort Dropdown */}
          <div style={{
            flex: '0 0 auto',
            minWidth: '180px',
            width: isMobile ? '100%' : '180px'
          }}>
            <Select
              value={sortBy}
              onChange={setSortBy}
              style={{ 
                width: '100%',
                height: '40px'
              }}
              suffixIcon={getSortIcon()}
            >
              <Select.Option value="recent">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ClockCircleOutlined />
                  <span>Recent First</span>
                </div>
              </Select.Option>
              <Select.Option value="nameAsc">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <SortAscendingOutlined />
                  <span>Name A-Z</span>
                </div>
              </Select.Option>
              <Select.Option value="nameDesc">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <SortDescendingOutlined />
                  <span>Name Z-A</span>
                </div>
              </Select.Option>
              <Select.Option value="progressDesc">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <SortDescendingOutlined />
                  <span>High Progress</span>
                </div>
              </Select.Option>
              <Select.Option value="progressAsc">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <SortAscendingOutlined />
                  <span>Low Progress</span>
                </div>
              </Select.Option>
            </Select>
          </div>
        </div>
      </Card>

      {/* Parayana Cards Grid */}
      {loading ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          height: '200px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🕉️</div>
            <Text style={{ color: '#FF8C00', fontSize: '16px' }}>Loading your parayanas...</Text>
          </div>
        </div>
      ) : filteredAndSortedParayanas.length === 0 ? (
        <Card style={{ 
          textAlign: 'center', 
          background: 'linear-gradient(135deg, #FFF8F0 0%, #FFFFFF 100%)',
          border: '2px dashed #FFB347',
          borderRadius: '16px'
        }}>
          <div style={{ padding: '40px 20px' }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>
              {searchText ? '🔍' : '🕉️'}
            </div>
            <Title level={3} style={{ color: '#FF8C00', marginBottom: '8px' }}>
              {searchText ? 'No parayanas found' : 'Start Your Spiritual Journey'}
            </Title>
            <Text style={{ color: '#666', fontSize: '16px', marginBottom: '24px', display: 'block' }}>
              {searchText 
                ? `No parayanas match "${searchText}". Try a different search term.`
                : 'Create your first parayana to begin tracking your spiritual progress'
              }
            </Text>
            {!searchText && (
              <Button
                type="primary"
                size="large"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingParayana(null);
                  setModalVisible(true);
                }}
                style={{
                  background: 'linear-gradient(135deg, #FF8C00 0%, #FF7F00 100%)',
                  borderColor: '#FF8C00',
                  height: '48px',
                  fontSize: '16px',
                  padding: '0 32px'
                }}
              >
                Create First Parayana
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <Row gutter={[12, 12]}>
          {filteredAndSortedParayanas.map((parayana) => (
            <Col 
              key={parayana.id}
              xs={24}
              sm={12}
              lg={8}
              xl={6}
            >
              <ParayanaCard parayana={parayana} />
            </Col>
          ))}
        </Row>
      )}

      <ParayanaFormModal
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingParayana(null);
        }}
        onSuccess={handleModalSuccess}
        editingParayana={editingParayana}
      />
    </div>
  );
};

export default ParayanaList; 